<template>
  <table :class="tableClasses">
    <!-- eslint-disable vue/no-v-html -->
    <thead v-if="headers && headers.length > 0">
      <tr>
        <th
          v-for="(header, index) in headers"
          :key="index"
          :class="[tableHeadingClasses, header?.class || '']"
        >
          <div class="flex items-center justify-between gap-2">
            <slot
              :name="`header-${index}-left-icon`"
              :value="header"
              :index="index"
            >
              <V2IconsDotsThreeVertical
                v-if="header.hasLeftIcon"
                :fill="headerIconFill"
                class="cursor-pointer"
              />
            </slot>

            <slot
              :name="`header-${index}`"
              :value="header"
              :index="index"
            >
              
              <div class="flex flex-col">
                <div
                  v-if="header.description"
                  class="font-normal text-xs"
                  v-html="header?.description"
                >
                </div>
                
                <div v-html="header.label">
                </div>
              </div>
            </slot>

            <slot
              :name="`header-${index}-right-icon`"
              :value="header"
              :index="index"
            >
              <V2IconsDotsThreeVertical
                v-if="header.hasRightIcon"
                class="cursor-pointer"
                :fill="headerIconFill"
              />
            </slot>
          </div>
        </th>
      </tr>
    </thead>

    <tbody>
      <tr v-for="(row, rowIndex) in items" :key="rowIndex">
        <td
          v-for="(cell, cellIndex) in row"
          :key="cellIndex"
          :class="cellClasses(rowIndex)"
          class="px-4 py-3"
        >
          <slot
            :name="`cell-${rowIndex}-${cellIndex}`"
            :value="cell"
            :row="row"
            :index="cellIndex"
          >
            <div class="flex items-center justify-between gap-2">
              <slot
                :name="`row-${rowIndex}cell-${cellIndex}-left-icon`"
                :value="cell"
                :cell-index="cellIndex"
                :row-index="rowIndex"
              >
                <V2IconsDotsThreeVertical
                  v-if="cell.hasLeftIcon"
                  :fill="cellIconFill"
                  class="cursor-pointer"
                />
              </slot>

              <slot
                :name="`cell-${cellIndex}`"
                :value="cell"
                :cell-index="cellIndex"
                :row-index="rowIndex"
              >
                <div class="flex flex-col">
                  <div v-if="cell.description" class="font-normal text-xs">
                    {{ cell.description }}
                  </div>
                  
                  <div v-html="cell.value" />
                </div>
              </slot>

              <slot
                :name="`row-${rowIndex}cell-${cellIndex}-right-icon`"
                :value="cell"
                :cell-index="cellIndex"
                :row-index="rowIndex"
              >
                <V2IconsDotsThreeVertical
                  v-if="cell.hasRightIcon"
                  class="cursor-pointer"
                  :fill="cellIconFill"
                />
              </slot>
            </div>
          </slot>
        </td>
      </tr>

      <tr v-if="items.length === 0">
        <td
          :colspan="headers ? headers.length : 1"
          class="px-4 py-2 text-center text-gray-500"
        >
          <slot name="empty">No data available.</slot>
        </td>
      </tr>
    </tbody>
  </table>
</template>

<script setup>
defineOptions({
  name: 'V2MoleculesTable'
})

const props = defineProps({
  headers: {
    type: Array,
    default: () => []
  },
  items: {
    type: Array,
    default: () => []
  },
  variant: {
    type: String,
    default: 'base',
    validator: value => ['base', 'transparent', 'alternate', 'highlight'].includes(value)
  },
  headingVariant: {
    type: String,
    default: 'base',
    validator: value => ['base', 'transparent', 'alternate', 'highlight'].includes(value)
  }
})

const headerIconFill = computed(() => {
  switch (props.variant) {
    case 'transparent':
      return semanticColorLookup('primary.default')
    case 'alternate':
      return semanticColorLookup('primary.variant-dark')
    case 'highlight':
      return semanticColorLookup('primary.on-light')
    case 'base':
    default:
      return semanticColorLookup('primary.on-light')
  }
})

const cellIconFill = computed(() => {
  switch (props.variant) {
    case 'transparent':
      return semanticColorLookup('primary.default')
    case 'alternate':
      return semanticColorLookup('primary.variant-dark')
    case 'base':
    case 'highlight':
      return semanticColorLookup('primary.default')
    default:
      return semanticColorLookup('primary.default')
  }
})

const tableClasses = computed(() => {
  const classes = ['text-sm/[150%] md:text-base/[150%] text-left']

  switch (props.variant) {
    case 'alternate':
      break
    case 'base':
    case 'transparent':
    case 'highlight':
    default:
      classes.push('bg-semantic-primary-on-light shadow-md rounded-md')
      break
  }

  return classes
})

const tableHeadingClasses = computed(() => {
  const classes = [
    'px-4 py-3 text-left border border-semantic-outline-white font-bold'
  ]

  switch (props.headingVariant) {
    case 'transparent':
      classes.push('bg-transparent text-semantic-surface-on-surface')
      break
    case 'alternate':
      classes.push(
        'bg-semantic-surface-container-high text-semantic-surface-on-surface'
      )
      break
    case 'highlight':
      classes.push(
        'bg-semantic-primary-default text-semantic-primary-on-light'
      )
      break
    case 'base':
    default:
      classes.push(
        'bg-semantic-primary-variant-light text-semantic-primary-on-light'
      )
      break
  }

  return classes
})

const cellClasses = rowIndex => {
  const classes = ['text-semantic-surface-on-surface', 'border border-semantic-outline-white']
  
  if (props.variant === 'base') {
    classes.push(
      'bg-semantic-surface-container-lowest'
    )
  }
  if (props.variant === 'highlight') {
    classes.push(
      'bg-semantic-surface-container-high'
    )
  }
  if (props.variant === 'alternate') {
    if (rowIndex % 2 !== 0) {
      classes.push(
        'bg-semantic-surface-container-low '
      )
    } else {
      classes.push(
        'bg-semantic-surface-container-lowest'
      )
    }
  }
  return classes
}
</script>
